<template>
	<ui-root :part="{ type: 'interface'}" :data="mailForm" :errs="errors">
	<div class="email-window-wrapper" v-bind="$attrs">
		<div v-if="showSent" class="mail-sent">
			<div><sym-icon name="email-out"/> {{env.msg("bulkMail.sent")}}</div>
			<div>
				<button class="button" @click.prevent="showSent = false">
					<sym-icon name="cross-16-heavy"/> <span>{{env.msg("butt.back")}}</span>
				</button>
			</div>
		</div>
		<div v-else class="email-window" :class="{'email-window-border': !window}">
			<div class="email-icon stamp">
				<Transition name="stamp">
					<div v-if="showStamp" class='stamp-wrapper'>
						<svg  viewBox="0 0 75 75"
							  xmlns="http://www.w3.org/2000/svg" 
							  xmlns:xlink="http://www.w3.org/1999/xlink" >
						  <g>
							<path d="M37.5,1C57.6,1,74,17.4,74,37.5S57.6,74,37.5,74S1,57.6,1,37.5S17.4,1,37.5,1 M37.5,0C16.8,0,0,16.8,0,37.5 S16.8,75,37.5,75S75,58.2,75,37.5S58.2,0,37.5,0L37.5,0z"/>
							<path d="M37.5,12.5c13.8,0,25,11.2,25,25s-11.2,25-25,25s-25-11.2-25-25S23.7,12.5,37.5,12.5 M37.5,11.5c-14.4,0-26,11.6-26,26 s11.6,26,26,26s26-11.6,26-26S51.9,11.5,37.5,11.5L37.5,11.5z"/>
						  </g>

						  <text text-anchor="middle" class="st0" x="37.5" y="41">{{ new Date().toLocaleDateString() }}</text>

						  <line class="st2" x1="16.5" y1="24" x2="58.5" y2="24"/>
						  <line class="st2" x1="16.5" y1="51" x2="58.5" y2="51"/>

						  <path id="stampPath" class="st3" d="M37.5,64.5c-14.9,0-27-12.1-27-27s12.1-27,27-27s27,12.1,27,27S52.4,64.5,37.5,64.5z"/>

						  <text text-anchor="middle">
							<textPath xlink:href="#stampPath" startOffset="50%">
							  <tspan class="st1">{{ mailStamp.text }}</tspan>
							</textPath>
						  </text>

						  <g fill="#333">
							<path d="M52.5,66.4l-2.8,1.1l0.2,3L48,68.2l-2.8,1.1l1.7-2.5l-1.9-2.4l2.9,0.8l1.7-2.5l0.2,3L52.5,66.4z"/>
							<path d="M41.6,69.9l-3,0l-0.9,2.9l-1-2.9l-3,0l2.4-1.8l-1-2.9l2.5,1.7l2.4-1.8l-0.9,2.9L41.6,69.9z"/>
							<path d="M29.9,69.4l-2.9-1l-1.9,2.4l0.1-3l-2.9-1l2.9-0.8l0.1-3l1.7,2.5l2.9-0.8l-1.9,2.4L29.9,69.4z"/>
						  </g>
						</svg>
					</div>
				</Transition>
			</div>
			<div class="email-header">
				<!-- <api-error v-for="e in errors" :level="e.level" :message="e.message"></api-error> -->
				<div class="input-head bottomline">
					<div class="label">{{env.msg("mailform.to")}}:</div>
					<mail-select-part ref="refRecipients" :part="recipientsDefinition('recipients', 10)" :data="mailForm" />
					<span class="sender-field">
						<Teleport defer to="#small_window_sender" :disabled="showSender">
							<div class="label2">{{env.msg("mailform.from")}}:</div>
							<select-part :part="senderAccountDefinition" :data="mailForm" />
						</Teleport>
					</span> 
				</div> 
				<div class="input-head">
					<div class="label">{{env.msg("mailform.cc")}}:</div>
					<mail-select-part :part="recipientsDefinition('recipientsCc', 20)" :data="mailForm" />
					<span v-if="!showBcc" class="link-like-fi" @click="showBcc=true">{{env.msg("mailform.bcc")}}</span>
				</div> 	
				<div class="input-head topline" v-if="showBcc">
					<div class="label">{{env.msg("mailform.bcc")}}:</div>
					<mail-select-part :part="recipientsDefinition('recipientsBcc', 30)" :data="mailForm" />
				</div> 						
				<div class="input-head topline">
					<div class="label">{{env.msg("mailform.subject")}}:</div>
					<input-part ref="refSubject" :part="subjectDefinition" :data="mailForm" />
					<div class="right-icon">
						<sym-icon v-show="mailForm.person || mailForm.branch || mailForm.customer" name="party" class="link-like-fi" 
						 	@click="env.navigateTo(SrcType.PARTY, mailForm.person || mailForm.branch || mailForm.customer || 0)" />
						<sym-icon v-show="mailForm.opportunity" name="opportunity" class="link-like-fi" 
							@click="env.navigateTo(SrcType.OPPORTUNITY, mailForm.opportunity ?? 0)" />
						<sym-icon v-show="mailForm.busCase" name="case" class="link-like-fi"
							@click="env.navigateTo(SrcType.BUSCASE, mailForm.busCase ?? 0)" />
						<sym-icon v-show="mailForm.problem" name="helpdesk" class="link-like-fi"
							@click="env.navigateTo(SrcType.PROBLEM, mailForm.problem ?? 0)" />
						<sym-icon v-show="mailForm.campaign" name="campaign" class="link-like-fi"
							@click="env.navigateTo(SrcType.CAMPAIGN, mailForm.campaign ?? 0)" />
						<sym-icon :name="showAdvancedIcon" :title="env.msg('mail.advanced')" class="link-like-fi adv-icon" @click="toggleAdvance"/>
					</div>
				</div> 
			</div>
			<div class="email-advanced">
				<div v-show="showAdvanced">
					<references-part :part="refDefinition" :data="mailForm" />
					<div class="input-group">
						<div id="small_window_sender" class="input-field"></div> 
						<div class="input-field">
							<div class="label2">{{env.msg("mail.priority")}}:</div>
							<select-part :part="priorityDefinition" :data="mailForm" />
						</div> 
						<div class="input-field">
							<div class="label2">{{env.msg("mail.sendAfter")}}:</div>
							<temporal-part :part="sendAfterDefinition" :data="mailForm" />
						</div>
						<div class="input-field">
							<div class="label2">{{env.msg("mail.visibleTo")}}:</div>
							<user-select-part v-if="!mailForm.refreshVisibility" :part="visibilitiesDefinition" :data="mailForm" />
							<label><input type="checkbox" v-model="mailForm.refreshVisibility" /> {{env.msg("mail.refreshVisibility")}}</label>
						</div>
						<div class="input-field">
							<div class="label2"></div>
							<label><input type="checkbox" v-model="mailForm.receipt" /> {{env.msg("mail.receipt")}}</label>
						</div>
					</div>
				</div>
			</div>
			<div class="email-body topline">
				<div class="fully">
					<html-area-part :part="bodyDefinition" :data="mailForm" style="height: 100%;" />
				</div>
			</div>
			<div class="email-footer">
				<div v-if="mailForm.mailIdent" class="attachments">
					<attachments v-model="mailForm.attachments" :mail-ident="mailForm.mailIdent"
							:errors="attErrors" ref="refAttachments" />
				</div>
				<div class="actions">
					<div class="mail-attach-zone" style="flex-grow: 1;">
						<file-zone-part :part="fileZoneDefinition" :data="mailForm" @uploaded="uploaded" />
					</div>
					<div class="mail-buttons">
						<button-part :part="sendDefinition" :data="mailForm" />
						<Transition name="fade">
							<div v-show="showSaving" class="mailSaving">
								<sym-icon class="uploadingIcon" name="refresh" /> {{env.msg("mail.saving")}}
							</div>
						</Transition>
					</div>
				</div>
			</div>
		</div>
		<!-- div>autosaveDraft:{{mailForm.autosaveDraft}}, draft: {{mailForm.draft}}, cmd:{{cmd}}</div -->
	</div>
	</ui-root>
</template>

<script setup lang="ts">
	import {reactive, ref, computed, watch, onMounted, onUnmounted, provide} from 'vue';
	import {connectEventBus, injectEnv} from "@fd/Environment.ts";
	import type {MailForm, MailStamp} from "@t/mail/MailForm.ts";
	import {MailFormSocket} from "./MailFormSocket.ts";
	import {SrcType} from "@t/meta/SourceType.ts";
	import type {Button} from "@t/fd/fixed/Button";
	import type {ErrorMap} from "@fd/PartInjectKeys.ts";
	
	import ApiError from "@/fd/helpers/ApiError.vue";
	import SymIcon from "@fd/helpers/SymIcon.vue";
	import MailSelectPart from '@/fd/editors/mail-select/MailSelectPart.vue';
	import Attachments from '@/vue/mail/Attachments.vue';
	import FileZonePart from '@/fd/editors/file/FileZonePart.vue';
	import HtmlAreaPart from '@/fd/editors/html/HtmlAreaPart.vue';
	import UiRoot from "@fd/containers/UiRoot.vue";
	import ReferencesPart from '@/fd/editors/reference/ReferencesPart.vue';
	import SelectPart from '@/fd/editors/SelectPart.vue';
	import TemporalPart from '@/fd/editors/temporal/TemporalPart.vue';
	import UserSelectPart from '@/fd/editors/user-select/UserSelectPart.vue';
	import InputPart from '@/fd/editors/InputPart.vue';
	import ButtonPart from '@/fd/editors/ButtonPart.vue';
	import {RefFactory} from "../agenda/RefFactory.ts";
	import {TemplateType} from "@t/template/Templates.ts";
	import {addTruthyToMap, AppContextMap, contextKey} from "@t/fd/AppContext.ts";
	import {ApiErrorResponse} from "@t/util/ApiError.ts";
	import {References} from "@t/fd/editor/References.ts";
	
	const props = defineProps<{
		mail: MailForm,
		window: boolean,
		cmd: string,
		params: {
			recipient?: string;
        	template?: number;
       		root?: number;
        	includeMembers?: boolean;
		}
	}>();
	
	const SESSION_ADVANCE = "crm_mailWindow_advance";
	const SESSION_FORM_SIZE = "crm_mailWindow";
	
	defineOptions({
		inheritAttrs: false
	})
	
	const SAVING_LIMIT = 5000; //10000
	
	const mailForm = ref<MailForm>({ ...props.mail });
		console.log("mailForm:"+JSON.stringify(mailForm.value));
	const env = injectEnv();
	const bus = connectEventBus("SendMailForm");
	
	const errors = reactive<ErrorMap>([]);
	const mailStamp = ref<MailStamp>({url:'',text:''});
	
	const refAttachments = ref<InstanceType<typeof Attachments> | null>(null);
	const refSubject = ref<InstanceType<typeof InputPart> | null>(null);
	const refRecipients = ref<InstanceType<typeof MailSelectPart> | null>(null);
	
	const showBcc = ref<boolean>(false);
	const showAdvanced = ref<boolean>(false);
	const showSaving = ref<boolean>(false);
	const showSent = ref<boolean>(false);
	const showSender = ref<boolean>(true);
	let mediaQuery: MediaQueryList;
	
	const savingId = ref(0);
	
	const refFactory = new RefFactory(env);
	const webSocket = ref<MailFormSocket|null>(null);
	
	/* app context for prosmirror editor */
	const ctx = computed<AppContextMap>(() => {
		const ctx:AppContextMap = {};
		addTruthyToMap(ctx, "mailIdent", mailForm.value.mailIdent);
		addTruthyToMap(ctx, "customer", mailForm.value.customer);
		addTruthyToMap(ctx, "branch", mailForm.value.branch);
		addTruthyToMap(ctx, "person", mailForm.value.person);
		addTruthyToMap(ctx, "busCase", mailForm.value.busCase);
		addTruthyToMap(ctx, "opportunity", mailForm.value.opportunity);
		addTruthyToMap(ctx, "problem",  mailForm.value.problem);
		addTruthyToMap(ctx, "campaign",  mailForm.value.campaign);
		return ctx;
	});
	provide(contextKey, ctx);
	
	const showAdvancedIcon = computed<string>(() => {
		return showAdvanced.value?"minus-square-14":"plus-square-14";
	});

	const showStamp = computed<boolean>(() => {
    	return (mailForm.value.recipients?.length ?? 0) > 0
				 && !!mailForm.value.subject;
	});
	
	const stampUrl = computed<string>(() => {
		return `url('${mailStamp.value.url}')`;
	});
	
	const attErrors = computed<ErrorMap>(() => {
		if (errors) {
			return errors.filter(item => item.field==='attachment');
		}
		return [];
	});
	
	function recipientsDefinition(targetProp: string, tabindex: number): Record<string, any> {
		return {
			property: targetProp,
			min: 0,
			max: 20,
			catch: targetProp,
			//tabindex: tabindex,
			propagate:[
				{ d: "customer", s: "customer", empty: "" }
			]
		};
	}
	
	const subjectDefinition = computed(() => {
		return {
			property: "subject",
			itype: "text",
			catch: "subject",
			focus: true,
			//tabindex: 40,
			placeholder: env.msg('mailForm.subject.placeholder'),
		};
	});
	
	const bodyDefinition = computed(() => {
		return {
			property: "body",
			name: "body",
			//tabindex: 50,
			readOnly: false,
			displayStyle: "box",
			toolbarMode: "embedded",
			versionProperty: "bodyVersion",
			templatesFor: TemplateType.COMPOSE_EMAIL,
			/*options: {  style: "height: 100%;", clazz: "input-group" },*/
			catch: "body"
		};
	});
	
	const senderAccountDefinition = computed(() => {
		return {
			property: "senderAccount",
			list: "com.anneca.intouch.list.MailAccountsSmtpList",
			cast: "integer",
			catch: "senderAccount",
			//tabindex: 60
		};
	});
	
	const priorityDefinition = computed(() => {
		return {
			property: "priority",
			list: "com.anneca.intouch.list.MailPriorityList",
			catch: "priority",
			//tabindex: 70
		};
	});
	
	const sendAfterDefinition = computed(() => {
		return {
			property: "sendAfter",
			timePolicy: 2,
			catch: "sendAfter",
			//tabindex: 80
		};
	});
	
	const visibilitiesDefinition = computed(() => {
		return {
			property: "visibilities",
			users: true,
			groups: true,
			parties: false,
			sysgroups: true,
			min: 0,
			max: 100,
			readOnly: mailForm.value.refreshVisibility,
			options: { style: "min-width: 100px;"},
			catch: "visibilities",
			//tabindex: 90
		};
	});
	
	const fileZoneDefinition = computed(() => {
		return {
			endpoint: "api/attachments",
			//endpointParams: "mailIdent="+mailForm.value.mailIdent,
			property: "mailIdent",
			showDrag: true,
			showDisc: true,
			showClip: true,
			showDoc: true,
			preview: false,
			bundleDrag: "mail.moveAttachmentsHere",
			options: {  style: "min-height: 70px;", clazz: "mailZone" }
		};
	});
	
	const sendDefinition = computed(() => {
		const subButtons = [];
		subButtons.push({ icon: "compose-email", label:env.msg("mailForm.createDraft"), action:"createDraft", title: "" });
		if ((mailForm.value.draft ?? 0) > 0 && !props.cmd) {
			subButtons.push({ icon: "compose-email", label:env.msg("mailForm.saveDraft"), action:"saveDraft" , title: "" });
		}
		
		return {
			label: env.msg("butt.send"),
			action: "send",
			mutating: false,
			//tabindex: 150,
			more: subButtons
		};
	});

	const refDefinition = computed<References>(() => {
		const srcTypes: Array<number> = [SrcType.PARTY];
		if (Server.mod.opportunity) srcTypes.push(SrcType.OPPORTUNITY);
		if (Server.mod.buscase) srcTypes.push(SrcType.BUSCASE);
		if (Server.mod.helpdesk) srcTypes.push(SrcType.PROBLEM);
		if (Server.mod.campaign) srcTypes.push(SrcType.CAMPAIGN);

		return {
			type: "references",
			items: refFactory.getRefs(srcTypes),
			options: { style: "padding-left: 8px; padding-right: 8px;"}
		};
	});
	
	function uploaded(): void {
		if (refAttachments.value) refAttachments.value.loadAttachments();
	}
	
	function checkOpened() {
		console.log("Signed to opened mail forms");
		let endpoint = "api/mailformmap/checkopened?mailIdent="+mailForm.value.mailIdent;
		env.net.fetch(endpoint)
			.then(response => {
				if (!response.ok) {
					console.error('Error:', response.status);
					errors.push({level:"SEVERE", message: env.msg("adminMessage.error.server")});
				}
			})
			.catch((error) => {
				console.log("Error saving mail form:"+error.message);
				errors.push({level:"SEVERE", message: env.msg("adminMessage.error.server")+error.message});
			});
	}

	function loadMailStamp() {
		env.net.fetch("api/mailformmap/stamp")
			.then((result) => result.json())
			.then(data => {
				mailStamp.value = data;
				//console.log("mailStamp:"+JSON.stringify(mailStamp.value));
			});
	}

	function loadForm(): Promise<boolean>  {
		errors.length = 0;
		let endpoint = "api/mailformmap/init";
		if (props.cmd) endpoint = env.net.urlParam(endpoint, "cmd", props.cmd);
		if (props.params.recipient) endpoint = env.net.urlParam(endpoint, "recipient", props.params.recipient);
		if (props.params.template) endpoint = env.net.urlParam(endpoint, "template", props.params.template);
		if (props.params.root) endpoint = env.net.urlParam(endpoint, "root", props.params.root);
		if (props.params.includeMembers) endpoint = env.net.urlParam(endpoint, "includeMembers", props.params.includeMembers);
		let requestMethod = "POST"
		if (props.mail?.mailIdent) { //if mailIdent exist at props, load and overwrite mf from mailformmap
			endpoint = "api/mailformmap/"+props.mail.mailIdent;
			requestMethod = "GET"
		}
		
		return env.net.fetch(endpoint, requestMethod, mailForm.value)
				.then((result) => result.json())
				.then(data => {
					Object.assign(mailForm.value, data);
					bus.fire("update", "body");
					if (data.problems) {
						console.log("problems:"+JSON.stringify(data.problems));
						errors.push(...data.problems);
					}
					return true;
				})
				.catch((error) => {
					console.log("Error init mail form:"+error.message);
					errors.push({level:"SEVERE", message: env.msg("adminMessage.error.server")+error.message});
					return false;
				})
	}
	
	function autosaveForm(): Promise<boolean> {
		//1.update subject on server
		updateSubject();
		//2.autosave
		return saveForm("api/mailformmap/"+mailForm.value.mailIdent);
	}
	
	function updateSubject():void {
		if (webSocket.value) {
			const msg = {
					type:"updateSubject",
					subject: mailForm.value.subject ?? ""
				};
			webSocket.value.sendMessage(msg);
		}
	}
	
	async function saveForm(endpoint: string): Promise<boolean>
	{
		//console.log("saving mailForm:"+mailForm.value.mailIdent+" / body:"+mailForm.value.body);
		bus.fire("flush", "body");
		showSaving.value=true;
		const nr = await env.net.fetch(endpoint,"PUT", mailForm.value);
		errors.length = 0;
		try {
			if (nr.ok) {
				const data = await nr.json() as MailForm;
				mailForm.value.autosaveDraft = data.autosaveDraft;
				return true;
			} else {
				const er = await nr.json() as ApiErrorResponse;
				console.error('Error:', nr.status);
				errors.push({type: "ERROR", level:"SEVERE", message: env.msg("adminMessage.error.concept")});
				if (er.problems) {
					errors.push(...er.problems);
				}
				return false;
			}
		} finally {
			setTimeout(() => { showSaving.value = false }, 1500);
		}
	}
	
	async function sendForm():Promise<boolean> {
		console.log("sending mailForm:"+mailForm.value.mailIdent);
		errors.length = 0;
		let endpoint = "api/mailformmap/"+mailForm.value.mailIdent+"/send";


		const nr = await env.net.fetch(endpoint,"POST", mailForm.value);
		if (nr.ok) {
			return true;
		} else {
			const er = await nr.json() as ApiErrorResponse;
			if (er.problems) {
				errors.push(...er.problems);
			}
			return false;
		}
		// errors.push({level:"SEVERE", message: env.msg("adminMessage.error.server")+error.message});
	}
	
	function toggleAdvance(): void {
		showAdvanced.value =! showAdvanced.value;
		localStorage.setItem(SESSION_ADVANCE, showAdvanced.value.toString());
	}
	
	function closeWindow(): void  {
		if (props.window) { 
			window.close();
		}
		else {
			showSent.value=true;
		}
	}
	
	function focusFormField(): void {
		if (!mailForm.value.recipients?.length) refRecipients.value?.focus();
		else if (!mailForm.value.subject) refSubject.value?.focus();
		else {
			bus.fire("focus", "body");
		}
	}
	
	function saveWindowSize(): void {
		if (props.window) {			
			const mailWindow = {
				left: window.screenX,
				top: window.screenY,
				width: Math.max(window.innerWidth, 500),
				height: Math.max(window.innerHeight, 300)
			};
			localStorage.setItem(SESSION_FORM_SIZE, JSON.stringify(mailWindow));
		}
	}
	
	bus.onCommand(["send","createDraft","saveDraft"], async (cmd:string) => {
			try {
				let success:boolean;
				if (cmd === "send") {
					success = await sendForm();
					if (!success) errors.push({ level:"SEVERE", message: env.msg("adminMessage.error.server") });
				} else {
					if (cmd === "createDraft") {
						mailForm.value.draft = 0;
					}
					success = await saveForm("api/mailformmap/" + mailForm.value.mailIdent + "/draft");
				}
				if (success) closeWindow();
				return success ? "ok" : "fail";
			} catch (e) {
				return "fail";
			}
		});

	bus.on("propagatetrinity", (trinity: {customer:number, branch:number, person:number}) => {
		if (!mailForm.value.customer && !mailForm.value.branch && !mailForm.value.person) {
			mailForm.value.customer = trinity.customer;
			mailForm.value.branch = trinity.branch;
			mailForm.value.person = trinity.person;
		}
	});
	
	//load before mount
	loadMailStamp();	
	
	watch(() => mailForm.value.subject, (newSubject) => {
		document.title = newSubject || env.msg("form.composeMail");
	});
	
	watch(() => mailForm, (newMailForm) => {
		//console.log("change detected:"+ savingId.value);
		//console.log("autosave disabled");
		if (savingId.value) clearTimeout(savingId.value);
		savingId.value = setTimeout(() => { autosaveForm(); }, SAVING_LIMIT);
	}, { deep: true });
	
	const checkMedia = (e: MediaQueryListEvent) => {
		showSender.value = e.matches
	}
	
	onMounted(() => {
		mediaQuery = window.matchMedia('(min-width: 768px)');
		showSender.value = mediaQuery.matches;
		mediaQuery.addEventListener('change', checkMedia);
		
		loadForm()
			.then((ok) => {
				if (ok) {
					focusFormField();	
					checkOpened();
					showAdvanced.value = localStorage.getItem(SESSION_ADVANCE) === 'true';
					if (!import.meta.env.DEV) { 
						webSocket.value = new MailFormSocket(mailForm.value.mailIdent);
						webSocket.value.onAttachmentRefresh = uploaded;
					}
					if (savingId.value) clearTimeout(savingId.value);
					console.log("mailForm initializing done, mailIdent:"+mailForm.value.mailIdent+" showAdv:"+showAdvanced.value);
				}
			});
		if (props.window) {
			window.addEventListener('beforeunload', saveWindowSize);
		}
	});
	
	onUnmounted(() => {
		if (mediaQuery) {
			mediaQuery.removeEventListener('change', checkMedia);
		}
		if (props.window) {
			window.removeEventListener('beforeunload', saveWindowSize);
		}
		webSocket.value?.disconnect();
	});
	
</script>

<style>
.email-window-wrapper {
	container-type: inline-size;
	container-name: email-window-container;
	height: 100vh;
	width: 100%;
}

.email-window {
  display: grid;
  grid-template-columns: 100px 1fr;
  grid-template-rows: minmax(110px, auto) auto 1fr auto;
  grid-template-areas:
	"rowIcon rowHeader"
	"rowAdvanced rowAdvanced"
    "rowBody rowBody"
    "rowFooter rowFooter";
  width: 100%;
  min-width: 400px;
  height: 100%;
  background: var(--form-bg);
  color: var(--form-fg);
  /*overflow: hidden;*/
}

.email-window-border {
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);	
}

@container email-window-container (max-width: 500px) {
  .email-window {
    grid-template-columns: 1fr;
    grid-template-areas: 
		"rowHeader"
		"rowAdvanced"
		"rowBody"
		"rowFooter";
  }
  .email-icon {
    display: none;
  }
}

.email-window .bottomline {
	border-bottom: 1px solid #eee;			
}

.email-window .topline {
	border-top: 1px solid #eee;			
}

.email-advanced {
	grid-area: rowAdvanced;	
}

.email-ref {
	padding: 10px 0;
}

.email-icon {
	grid-area: rowIcon;
	padding: 10px 0;
}

.email-header {
	grid-area: rowHeader;
	padding: 10px 0px;
  
}

.email-body {
	grid-area: rowBody;
	padding: 4px 0px;
	overflow: auto;
}

.email-body .fully {
	height: 100%;
}

.email-footer {
	grid-area: rowFooter;
	padding: 0px 15px 15px 15px;
	border-bottom-left-radius: 8px;
	border-bottom-right-radius: 8px;
}

.input-group {
	padding: 4px 8px;
}

.input-head {
	padding: 5px 8px;
	display: flex;
	align-items: baseline;
	gap: 8px;
}

.input-head input {
  flex: 1;
  border: none  !important;
  box-shadow: none !important;
  outline: none  !important;
}

.input-head input:focus { 
	outline: none  !important;
    box-shadow: none  !important;
}

.sender-field {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.input-field {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 4px 0; 
}

.input-field .label {
	width: 60px;
	opacity: 0.8;
}

.input-field .label2 {
	width: 80px;
	opacity: 0.8;
}

.attachments {
	padding: 4px 0;
}

.actions {
  display: flex;
  gap: 8px;
  justify-content: space-between;
}

.mail-buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: space-between;	
}

.send-button {
    padding: 0 4px;
}

.right-icon {
	display: flex;
	flex-direction: row;
	gap: 16px;
	justify-content: flex-end;
}

.stamp {
	border-collapse: collapse;
	width: 100%;
	background-image: v-bind(stampUrl);
	background-size: 103px 119px;
	background-repeat: no-repeat;
	background-position: 0px 0px;
}

.mailSaving {
	color: var(--fg);
	padding-left: var(--gap);
}

.mail-sent {
	height: 100%;
	display: flex;
	gap: 8px;
	flex-direction: column;
	justify-content: center;
	align-items: baseline;
	font-size: var(--font-size-xxl);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.stamp-wrapper .st0 {
  font-family: sans-serif;
  font-size: 11px;
  fill: black;
}
.stamp-wrapper .st1 {
  font-family: sans-serif;
  font-size: 10px;
  fill: black;
}
.stamp-wrapper.st2 {
  fill: none;
  stroke: #000000;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-miterlimit: 10;
}
.stamp-wrapper .st3 {
  fill: none;
}

.stamp-wrapper {
	position: relative;
	pointer-events: none;
	z-index: 1000;
	width: 75px;
	height: 75px;
	opacity: 0.6;
	transform: scale(2) rotate(0deg);
	top: 45px;
	left: 30px;
	transition: all 0.3s ease-out;
	transform: rotate(-15deg); /* Cílová rotace */
}

/* Stav PŘED vstupem a PO odchodu (neviditelný stav) */
.stamp-enter-from,
.stamp-leave-to {
  opacity: 0;
  transform: rotate(0deg) scale(1.5); /* Začíná bez rotace a je větší */
}

/* Stav BĚHEM animace */
.stamp-enter-active {
  transition: all 0.2s ease-out; /* Rychlý "příklep" */
}

.stamp-leave-active {
  transition: all 0.5s ease-in; /* Pomalejší zmizení */
}

.adv-icon {
	font-weight: bold;
}

</style>